'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef } from 'react';
const GRADIENT_STOPS = [[0, 1], [7.142857142857142, 0.9789983639784623], [14.285714285714285, 0.9088278450944927], [21.428571428571427, 0.787619875773089], [28.57142857142857, 0.6357943292532582], [35.714285714285715, 0.48571949984761886], [42.857142857142854, 0.35784565916401334], [50, 0.2546057917934377], [57.14285714285714, 0.17447253499994986], [64.28571428571429, 0.11349623920978047], [71.42857142857143, 0.06814405260956846], [78.57142857142857, 0.03632969091995619], [85.71428571428571, 0.01520817299024202], [92.85714285714286, 0.003670836304081604], [100, 0]] as const;
const LERP_SPEED = 0.06;
function buildHighlightGrid(opacity: number) {
  return [`linear-gradient(rgba(255, 255, 255, ${opacity}) 1px, transparent 1px)`, `linear-gradient(90deg, rgba(255, 255, 255, ${opacity}) 1px, transparent 1px)`].join(', ');
}
interface SpotlightGridProps {
  /** Radius of the spotlight circle in px */
  spotlightRadius?: number;
  /** Size of each grid cell in px */
  gridSize?: number;
  /** CSS selector passed to `closest()` to find the mouse-tracking container */
  containerSelector?: string;
  /** Opacity of the highlight grid lines (0–1) */
  highlightOpacity?: number;
  /** Whether to render a faint always-visible base grid underneath */
  showBaseGrid?: boolean;
}
function SpotlightGrid(t0) {
  const $ = _c(17);
  const {
    spotlightRadius: t1,
    gridSize: t2,
    containerSelector: t3,
    highlightOpacity: t4,
    showBaseGrid: t5
  } = t0;
  const spotlightRadius = t1 === undefined ? 90 : t1;
  const gridSize = t2 === undefined ? 12 : t2;
  const containerSelector = t3 === undefined ? "section" : t3;
  const highlightOpacity = t4 === undefined ? 0.5 : t4;
  const showBaseGrid = t5 === undefined ? false : t5;
  const spotRef = useRef(null);
  const rafId = useRef(0);
  let t6;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      x: 0,
      y: 0
    };
    $[0] = t6;
  } else {
    t6 = $[0];
  }
  const target = useRef(t6);
  let t7;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      x: 0,
      y: 0
    };
    $[1] = t7;
  } else {
    t7 = $[1];
  }
  const current = useRef(t7);
  const active = useRef(false);
  let t8;
  let t9;
  if ($[2] !== containerSelector || $[3] !== spotlightRadius) {
    t8 = () => {
      const spot = spotRef.current;
      if (!spot) {
        return;
      }
      const container = spot.closest(containerSelector);
      if (!container) {
        return;
      }
      const buildMask = function buildMask(x, y) {
        const stops = GRADIENT_STOPS.map(_temp).join(", ");
        return `radial-gradient(${spotlightRadius}px circle at ${x}px ${y}px, ${stops})`;
      };
      function tick() {
        if (!spot || !active.current) {
          return;
        }
        current.current.x = current.current.x + (target.current.x - current.current.x) * LERP_SPEED;
        current.current.y = current.current.y + (target.current.y - current.current.y) * LERP_SPEED;
        const mask = buildMask(current.current.x, current.current.y);
        spot.style.maskImage = mask;
        spot.style.webkitMaskImage = mask;
        rafId.current = requestAnimationFrame(tick);
      }
      const onMove = function onMove(e) {
        const rect = spot.getBoundingClientRect();
        target.current.x = e.clientX - rect.left;
        target.current.y = e.clientY - rect.top;
        if (!active.current) {
          current.current.x = target.current.x;
          current.current.y = target.current.y;
          active.current = true;
          spot.style.opacity = "1";
          rafId.current = requestAnimationFrame(tick);
        }
      };
      const onLeave = function onLeave() {
        active.current = false;
        cancelAnimationFrame(rafId.current);
        if (!spot) {
          return;
        }
        spot.style.opacity = "0";
      };
      container.addEventListener("mousemove", onMove as EventListener);
      container.addEventListener("mouseleave", onLeave);
      return () => {
        active.current = false;
        cancelAnimationFrame(rafId.current);
        container.removeEventListener("mousemove", onMove as EventListener);
        container.removeEventListener("mouseleave", onLeave);
      };
    };
    t9 = [containerSelector, spotlightRadius];
    $[2] = containerSelector;
    $[3] = spotlightRadius;
    $[4] = t8;
    $[5] = t9;
  } else {
    t8 = $[4];
    t9 = $[5];
  }
  useEffect(t8, t9);
  const sizeValue = `${gridSize}px ${gridSize}px`;
  let t10;
  if ($[6] !== showBaseGrid || $[7] !== sizeValue) {
    t10 = showBaseGrid && <div className="pointer-events-none absolute inset-0 opacity-5" style={{
      backgroundImage: ["linear-gradient(var(--border) 1px, transparent 1px)", "linear-gradient(90deg, var(--border) 1px, transparent 1px)"].join(", "),
      backgroundSize: sizeValue
    }} aria-hidden={true} />;
    $[6] = showBaseGrid;
    $[7] = sizeValue;
    $[8] = t10;
  } else {
    t10 = $[8];
  }
  let t11;
  if ($[9] !== highlightOpacity) {
    t11 = buildHighlightGrid(highlightOpacity);
    $[9] = highlightOpacity;
    $[10] = t11;
  } else {
    t11 = $[10];
  }
  let t12;
  if ($[11] !== sizeValue || $[12] !== t11) {
    t12 = <div ref={spotRef} className="pointer-events-none absolute inset-0 transition-opacity duration-700 ease-out" style={{
      backgroundImage: t11,
      backgroundSize: sizeValue,
      opacity: 0
    }} aria-hidden={true} />;
    $[11] = sizeValue;
    $[12] = t11;
    $[13] = t12;
  } else {
    t12 = $[13];
  }
  let t13;
  if ($[14] !== t10 || $[15] !== t12) {
    t13 = <>{t10}{t12}</>;
    $[14] = t10;
    $[15] = t12;
    $[16] = t13;
  } else {
    t13 = $[16];
  }
  return t13;
}
function _temp(t0) {
  const [pct, alpha] = t0;
  return `rgba(255,255,255,${alpha}) ${pct}%`;
}
export { SpotlightGrid };
export type { SpotlightGridProps };